import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Send, Copy, Download, Link2, QrCode, Sparkles, Search,
  Plus, Trash2, Mail, ExternalLink, Loader2, Target, Users, Wand2,
  Briefcase, FileSpreadsheet, Filter, Zap, X, Vote, GitBranch, CheckCircle2, XCircle,
  Layers, Radar, Eye, Building2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { fireConfetti } from "../components/ConfettiBurst";
import NovaVotingPanel from "../components/nova/NovaVotingPanel";
import NovaVotingCriteriaEditor from "../components/nova/NovaVotingCriteriaEditor";
import NovaShortlistPanel from "../components/nova/NovaShortlistPanel";
import NovaVoterCoherencePanel from "../components/nova/NovaVoterCoherencePanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PRIORITIES = { alta: "#dc2626", media: "#f59e0b", baja: "#0ea5e9" };
const STATUSES = ["backlog","votado","priorizado","en_portfolio","cerrado","en_curso"];
const STATUS_LABELS = {
  backlog: "Backlog Filtrado",
  votado: "Validado Innovación",
  priorizado: "Validado Comité",
  en_portfolio: "En Portfolio",
  cerrado: "Cerrado",
  en_curso: "En Curso",
};
const PHASES = [
  { key: "cribado", label: "Cribado", color: "#0ea5e9", icon: Filter, description: "Recepción y primer filtro de retos" },
  { key: "diferenciacion", label: "Diferenciación", color: "#f59e0b", icon: GitBranch, description: "Decisión GO / NO GO" },
  { key: "comite", label: "Comité", color: "#10b981", icon: Vote, description: "Votación por criterios y shortlist" },
];

export default function NovaCampaignPage() {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("retos");
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [filters, setFilters] = useState({ area: "", champion: "", status: "", priority: "", minVotes: "", hasMatch: "", phase: "", goNoGo: "" });
  const [sortBy, setSortBy] = useState("recent"); // recent | priority | votes | title | committee
  const [bulkRunning, setBulkRunning] = useState(false);

  const load = async () => {
    const r = await fetch(`${BACKEND_URL}/api/nova/campaigns/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    setC(await r.json());
    setLoading(false);
  };
  useEffect(() => { load(); }, [id, token]); // eslint-disable-line

  const challenges = useMemo(() => {
    if (!c?.challenges) return [];
    const q = search.trim().toLowerCase();
    const filtered = c.challenges.filter((ch) => {
      if (q && !(ch.title + " " + (ch.champion||"") + " " + (ch.area||"") + " " + (ch.category||"")).toLowerCase().includes(q)) return false;
      if (filters.area && !(ch.area||"").toLowerCase().includes(filters.area.toLowerCase())) return false;
      if (filters.champion && !(ch.champion||"").toLowerCase().includes(filters.champion.toLowerCase())) return false;
      if (filters.status && ch.status !== filters.status) return false;
      if (filters.priority && ch.priority !== filters.priority) return false;
      if (filters.minVotes && Number(ch.votes||0) < Number(filters.minVotes)) return false;
      if (filters.phase && (ch.phase || "cribado") !== filters.phase) return false;
      if (filters.goNoGo && ch.go_no_go !== filters.goNoGo) return false;
      if (filters.hasMatch === "con") {
        if (!(ch.status === "en_portfolio" || (ch.matches||[]).length > 0)) return false;
      }
      if (filters.hasMatch === "sin") {
        if ((ch.matches||[]).length > 0 || ch.status === "en_portfolio") return false;
      }
      return true;
    });
    const PRIORITY_ORDER = { alta: 0, media: 1, baja: 2 };
    const sorted = [...filtered];
    if (sortBy === "priority") sorted.sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9));
    else if (sortBy === "votes") sorted.sort((a, b) => (b.votes || 0) - (a.votes || 0));
    else if (sortBy === "title") sorted.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
    else if (sortBy === "committee") sorted.sort((a, b) => (b.committee_score?.overall || 0) - (a.committee_score?.overall || 0));
    else sorted.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return sorted;
  }, [c, search, filters, sortBy]);

  const uniqueAreas = useMemo(() => Array.from(new Set((c?.challenges||[]).map(x=>x.area).filter(Boolean))).sort(), [c]);
  const uniqueChampions = useMemo(() => Array.from(new Set((c?.challenges||[]).map(x=>x.champion).filter(Boolean))).sort(), [c]);

  const publicUrl = c ? `${window.location.origin}/nova/c/${c.public_token}` : "";

  const exportExcel = () => {
    // Simple CSV that Excel opens natively (BOM for UTF-8)
    const rows = challenges.map(ch => ({
      Título: ch.title || "",
      Área: ch.area || "",
      Champion: ch.champion || "",
      Categoría: ch.category || "",
      Prioridad: ch.priority || "",
      Estado: ch.status || "",
      Votos: ch.votes || 0,
      "Matches IA": (ch.matches || []).length,
      "Scouting IA": (ch.scouting?.results || []).length,
      Creado: (ch.created_at || "").slice(0, 10),
      "Iniciativa vinculada": ch.linked_initiative_id || "",
      Descripción: (ch.description || "").replace(/\n/g, " ").slice(0, 500),
    }));
    if (!rows.length) { toast.error("Sin retos con los filtros actuales"); return; }
    const headers = Object.keys(rows[0]);
    const esc = (v) => {
      const s = String(v ?? "");
      if (s.includes(";") || s.includes("\n") || s.includes('"')) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const csv = [headers.join(";"), ...rows.map(r => headers.map(h => esc(r[h])).join(";"))].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `NOVA_${(c?.name || "campaign").replace(/[^a-zA-Z0-9]/g, "_")}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(`${rows.length} retos exportados`);
  };

  const aiMatch = async (chId) => {
    setBusyId(chId+":match");
    try {
      const r = await fetch(`${BACKEND_URL}/api/nova/challenges/${chId}/match`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      toast.success(`IA encontró ${d.matches?.length||0} matches`);
      load();
    } catch { toast.error("Error matching IA"); }
    finally { setBusyId(null); }
  };
  const aiScout = async (chId) => {
    setBusyId(chId+":scout");
    try {
      const r = await fetch(`${BACKEND_URL}/api/nova/challenges/${chId}/scout`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }
      });
      if (!r.ok) throw new Error();
      toast.success("Scouting completado con Perplexity");
      load();
    } catch { toast.error("Scouting falló (revisa PERPLEXITY_API_KEY)"); }
    finally { setBusyId(null); }
  };
  const updateChallenge = async (chId, payload) => {
    await fetch(`${BACKEND_URL}/api/nova/challenges/${chId}`, {
      method: "PUT", headers: { "Content-Type":"application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload)
    });
    load();
  };

  const createInitiative = async (chId) => {    if (!window.confirm("¿Crear iniciativa en el Portfolio a partir de este reto?")) return;
    setBusyId(chId+":init");
    try {
      const r = await fetch(`${BACKEND_URL}/api/nova/challenges/${chId}/create-initiative`, {
        method: "POST", headers: { "Content-Type":"application/json", Authorization:`Bearer ${token}` },
        body: JSON.stringify({})
      });
      const d = await r.json();
      if (r.ok) {
        fireConfetti();
        toast.success(`🎉 ¡Iniciativa creada! ${d.initiative.name.slice(0, 50)}…`, {
          action: { label: "Ver portfolio", onClick: () => navigate("/portfolio") },
          duration: 6000,
        });
        load();
      } else toast.error("Error al crear iniciativa");
    } finally { setBusyId(null); }
  };

  const linkMatch = async (chId, match) => {
    if (!match?.id) { toast.error("Match sin id — recomienda re-matchear con IA"); return; }
    if (!window.confirm(`¿Vincular este reto a "${match.name}" existente en el portfolio?`)) return;
    setBusyId(chId + ":link");
    try {
      const r = await fetch(`${BACKEND_URL}/api/nova/challenges/${chId}/link-match`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ match_id: match.id, kind: match.kind || "initiative", name: match.name }),
      });
      const d = await r.json();
      if (r.ok) {
        fireConfetti();
        toast.success(`🔗 Vinculado a "${d.target_name || match.name}"`, {
          action: { label: "Ver portfolio", onClick: () => navigate("/portfolio") },
          duration: 5000,
        });
        load();
      } else toast.error(d.detail || "Error al vincular");
    } finally { setBusyId(null); }
  };

  const matchBulkAlta = async () => {
    if (!window.confirm("Re-matchear con IA TODOS los retos de prioridad alta sin matches. Puede tardar 1-2 min.")) return;
    setBulkRunning(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/nova/campaigns/${id}/match-bulk`, {
        method: "POST", headers: { "Content-Type":"application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ priority: "alta", only_empty: true })
      });
      const d = await r.json();
      toast.success(`${d.matched}/${d.processed} retos matcheados con IA`);
      load();
    } catch { toast.error("Error en bulk matching"); }
    finally { setBulkRunning(false); }
  };

  if (loading || !c) return <div className="text-muted-foreground">Cargando campaña…</div>;

  const stats = {
    total: c.challenges.length,
    alta: c.challenges.filter(x=>x.priority==="alta").length,
    enPortfolio: c.challenges.filter(x=>x.status==="en_portfolio"||(x.matches||[]).length>0).length,
    sinMatch: c.challenges.filter(x=>!(x.matches||[]).length).length,
  };

  const phaseStats = PHASES.reduce((acc, p) => {
    const list = c.challenges.filter(x => (x.phase || "cribado") === p.key);
    acc[p.key] = {
      total: list.length,
      go: list.filter(x => x.go_no_go === "go").length,
      no_go: list.filter(x => x.go_no_go === "no_go").length,
      pending: list.filter(x => !x.go_no_go).length,
    };
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="nova-campaign-page">
      <button onClick={() => navigate("/nova")} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
        <ArrowLeft className="w-4 h-4"/> NOVA
      </button>

      {/* Header */}
      <div className="rounded-2xl p-6 md:p-8 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #04140a 0%, #0b3b22 60%, #00873a 100%)" }}>
        <div className="text-emerald-300 text-xs uppercase tracking-[0.3em] font-medium">{c.area} · {c.year}</div>
        <h1 className="text-3xl md:text-4xl font-black text-white mt-1 tracking-tight">{c.name}</h1>
        <p className="text-emerald-100/85 max-w-3xl mt-2 text-sm">{c.description}</p>
        <div className="mt-5 flex flex-wrap gap-2 text-sm" data-testid="nova-kpi-filters">
          <StatButton
            label="Retos totales" value={stats.total} color="#86efac"
            active={!filters.priority && filters.hasMatch === "" && !filters.phase}
            onClick={() => setFilters({ ...filters, priority: "", hasMatch: "", phase: "" })}
            testid="kpi-total"
          />
          {PHASES.map((p) => {
            const st = phaseStats[p.key];
            return (
              <StatButton
                key={p.key}
                label={p.label}
                value={st.total}
                color={p.color}
                icon={p.icon}
                active={filters.phase === p.key}
                onClick={() => setFilters({ ...filters, phase: filters.phase === p.key ? "" : p.key })}
                testid={`kpi-phase-${p.key}`}
                subtitle={p.key === "diferenciacion" ? `${st.go} GO · ${st.no_go} NO GO` : p.key === "comite" ? `${st.go} shortlist` : `${st.pending} pendientes`}
              />
            );
          })}
          <StatButton
            label="Con match" value={stats.enPortfolio} color="#3BAD2D"
            active={filters.hasMatch === "con"}
            onClick={() => setFilters({ ...filters, hasMatch: filters.hasMatch === "con" ? "" : "con" })}
            testid="kpi-con-match"
          />
          <StatButton
            label="Sin match" value={stats.sinMatch} color="#f87171"
            active={filters.hasMatch === "sin"}
            onClick={() => setFilters({ ...filters, hasMatch: filters.hasMatch === "sin" ? "" : "sin" })}
            testid="kpi-sin-match"
          />
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" data-testid="copy-public-url" onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Enlace público copiado"); }} className="gap-2">
            <Link2 className="w-4 h-4"/>Copiar enlace público
          </Button>
          <Button size="sm" variant="secondary" data-testid="download-qr" onClick={() => window.open(`${BACKEND_URL}/api/nova/campaigns/${c.id}/qr.png?base_url=${encodeURIComponent(window.location.origin)}`, "_blank")} className="gap-2">
            <QrCode className="w-4 h-4"/>QR del enlace
          </Button>
          <a href={publicUrl} target="_blank" rel="noreferrer">
            <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2"><ExternalLink className="w-4 h-4"/>Abrir landing</Button>
          </a>
        </div>
        {c.public_password_hint && (
          <div className="mt-3 text-xs text-emerald-100/80">Contraseña inicial: <code className="px-2 py-0.5 rounded bg-white/10">{c.public_password_hint}</code> · cámbiala antes de invitar al equipo.</div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-border flex gap-4 overflow-x-auto">
        {["retos","portfolio","dealflow","scouting","observatorio","invitados","email","respuestas"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
            className={`pb-2 px-1 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${tab===t?"border-emerald-500 text-foreground":"border-transparent text-muted-foreground hover:text-foreground"}`}>
            {t === "retos" && <Target className="w-4 h-4 inline mr-1.5 -mt-0.5"/>}
            {t === "portfolio" && <Briefcase className="w-4 h-4 inline mr-1.5 -mt-0.5"/>}
            {t === "dealflow" && <Layers className="w-4 h-4 inline mr-1.5 -mt-0.5"/>}
            {t === "scouting" && <Radar className="w-4 h-4 inline mr-1.5 -mt-0.5"/>}
            {t === "observatorio" && <Eye className="w-4 h-4 inline mr-1.5 -mt-0.5"/>}
            {t === "invitados" && <Users className="w-4 h-4 inline mr-1.5 -mt-0.5"/>}
            {t === "email" && <Mail className="w-4 h-4 inline mr-1.5 -mt-0.5"/>}
            {t === "respuestas" && <Sparkles className="w-4 h-4 inline mr-1.5 -mt-0.5"/>}
            {t.charAt(0).toUpperCase()+t.slice(1)}
          </button>
        ))}
      </div>

      {tab === "retos" && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 justify-between">
            <div className="relative max-w-md flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"/>
              <input className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background text-sm" placeholder="Buscar reto, área, champion…" value={search} onChange={(e)=>setSearch(e.target.value)} data-testid="search-retos"/>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1" title="Ordena los retos abajo por el criterio elegido">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Ordenar:</span>
                <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="text-xs h-8 rounded-lg border border-border bg-background px-2" data-testid="sort-select">
                  <option value="recent" title="Más recientes primero según fecha de creación">Más recientes</option>
                  <option value="priority" title="Alta → Media → Baja">Prioridad</option>
                  <option value="votes" title="Más votos primero (votación pública)">Votos</option>
                  <option value="title" title="Orden alfabético por título">Título A-Z</option>
                  <option value="committee" title="Score del comité más alto primero">Score comité</option>
                </select>
              </div>
              <Button size="sm" variant="outline" onClick={exportExcel} data-testid="export-excel" className="gap-2">
                <Sparkles className="w-4 h-4"/>Exportar Excel ({challenges.length})
              </Button>
              <Button onClick={matchBulkAlta} disabled={bulkRunning} data-testid="bulk-match" className="bg-violet-600 hover:bg-violet-700 text-white gap-2">
                {bulkRunning ? <Loader2 className="w-4 h-4 animate-spin"/> : <Zap className="w-4 h-4"/>}
                Re-matchear prioridad alta
              </Button>
            </div>
          </div>
          <FiltersBar filters={filters} setFilters={setFilters} areas={uniqueAreas} champions={uniqueChampions} total={c.challenges.length} filtered={challenges.length}/>
          <NovaShortlistPanel campaign={c} token={token} onReload={load}/>
          <NovaVoterCoherencePanel campaignId={c.id} token={token}/>
          <NovaVotingCriteriaEditor campaignId={c.id} criteria={c.voting_criteria || []} token={token} onSaved={load}/>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {challenges.map((ch) => (
              <ChallengeCard
                key={ch.id} ch={ch} busy={busyId}
                onMatch={() => aiMatch(ch.id)} onScout={() => aiScout(ch.id)}
                onUpdate={(p) => updateChallenge(ch.id, p)}
                onCreateInitiative={() => createInitiative(ch.id)}
                onLinkMatch={(m) => linkMatch(ch.id, m)}
                votingCriteria={c.voting_criteria || []}
                token={token}
                onReload={load}
              />
            ))}
            {challenges.length === 0 && <div className="col-span-2 text-center text-muted-foreground text-sm py-10 border border-dashed border-border rounded-2xl">Sin resultados con los filtros actuales.</div>}
          </div>
        </div>
      )}

      {tab === "portfolio" && <PortfolioTab campaign={c} challenges={c.challenges.filter(x => x.linked_initiative_id || x.status === "en_portfolio")}/>}
      {tab === "dealflow" && <DealflowTab campaign={c} token={token} onReload={load}/>}
      {tab === "scouting" && <ScoutingTab campaign={c}/>}
      {tab === "observatorio" && <ObservatorioTab campaign={c} token={token}/>}
      {tab === "invitados" && <InviteesTab campaign={c} token={token} onChange={load}/>}
      {tab === "email" && <EmailTab campaign={c} token={token}/>}
      {tab === "respuestas" && <SubmissionsTab campaign={c}/>}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-white/90 text-xs flex items-center gap-1.5">
      <span className="w-1.5 h-1.5 rounded-full" style={{background: color || "#86efac"}}/>
      <strong>{value}</strong> {label}
    </div>
  );
}

function StatButton({ label, value, color, active, onClick, testid, icon: Icon, subtitle }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1.5 rounded-full border text-xs flex items-center gap-1.5 transition-all cursor-pointer ${active ? "bg-white/25 border-white text-white shadow-lg scale-105 font-bold" : "bg-white/10 border-white/15 text-white/90 hover:bg-white/20"}`}
      title={active ? "Click para desactivar el filtro" : "Click para filtrar por " + label.toLowerCase()}
    >
      {Icon ? <Icon className="w-3.5 h-3.5" style={{color: color || "#86efac"}}/> : <span className="w-1.5 h-1.5 rounded-full" style={{background: color || "#86efac"}}/>}
      <strong>{value}</strong> {label}
      {subtitle && <span className="text-[9px] opacity-70 ml-1">({subtitle})</span>}
      {active && <span className="text-[9px] ml-1">✕</span>}
    </button>
  );
}

function ChallengeCard({ ch, busy, onMatch, onScout, onUpdate, onCreateInitiative, onLinkMatch, votingCriteria, token, onReload }) {
  const matches = ch.matches || [];
  const scouting = ch.scouting?.results || [];
  const linkedId = ch.linked_initiative_id;
  const phase = ch.phase || "cribado";
  const phaseCfg = PHASES.find(p => p.key === phase) || PHASES[0];
  const PhaseIcon = phaseCfg.icon;
  const isDiff = phase === "diferenciacion";
  const isCommittee = phase === "comite";
  const score = ch.committee_score || { count: 0, overall: 0 };

  const nextPhase = () => {
    const idx = PHASES.findIndex(p => p.key === phase);
    if (idx < PHASES.length - 1) onUpdate({ phase: PHASES[idx + 1].key });
  };
  const prevPhase = () => {
    const idx = PHASES.findIndex(p => p.key === phase);
    if (idx > 0) onUpdate({ phase: PHASES[idx - 1].key });
  };

  return (
    <article className="rounded-2xl border border-border bg-card overflow-hidden" data-testid={`challenge-${ch.id}`}>
      <div className="p-4 border-b border-border flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="w-2 h-2 rounded-full" style={{background: PRIORITIES[ch.priority] || "#94a3b8"}}/>
            <code className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">#{ch.n}</code>
            <span className="text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1 font-bold" style={{background: `${phaseCfg.color}20`, color: phaseCfg.color}} title={phaseCfg.description}>
              <PhaseIcon className="w-3 h-3"/>{phaseCfg.label}
            </span>
            {ch.go_no_go === "go" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 font-bold flex items-center gap-1"><CheckCircle2 className="w-3 h-3"/>GO</span>}
            {ch.go_no_go === "no_go" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/15 text-red-700 dark:text-red-400 font-bold flex items-center gap-1"><XCircle className="w-3 h-3"/>NO GO</span>}
            {ch.area && <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{ch.area}</span>}
            {ch.votes>0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">{ch.votes} votos</span>}
            {score.count > 0 && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-700 dark:text-violet-300 font-bold flex items-center gap-1" title={`${score.count} miembros del comité han votado`}>
                <Vote className="w-3 h-3"/>{score.overall}/5
              </span>
            )}
          </div>
          <h4 className="font-semibold text-sm leading-snug">{ch.title}</h4>
          {ch.champion && <p className="text-xs text-muted-foreground mt-1">👤 {ch.champion}</p>}
        </div>
        <div className="flex flex-col gap-1">
          <select className="text-xs px-2 py-1 rounded border border-border bg-background" value={ch.status} onChange={(e)=>onUpdate({status:e.target.value})} data-testid={`status-${ch.id}`}>
            {STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s] || s.replace("_"," ")}</option>)}
          </select>
          <select className="text-xs px-2 py-1 rounded border border-border bg-background" value={phase} onChange={(e)=>onUpdate({phase:e.target.value})} data-testid={`phase-${ch.id}`}>
            {PHASES.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
        </div>
      </div>

      <div className="p-4 space-y-3">
        {/* Phase workflow controls */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="ghost" onClick={prevPhase} disabled={PHASES.findIndex(p=>p.key===phase)===0} className="text-xs h-7" data-testid={`phase-prev-${ch.id}`}>
            ← Fase anterior
          </Button>
          <Button size="sm" variant="ghost" onClick={nextPhase} disabled={PHASES.findIndex(p=>p.key===phase)===PHASES.length-1} className="text-xs h-7" data-testid={`phase-next-${ch.id}`}>
            Fase siguiente →
          </Button>
          {isDiff && (
            <>
              <Button size="sm" onClick={() => onUpdate({ go_no_go: ch.go_no_go === "go" ? null : "go" })} className={`text-xs h-7 gap-1 ${ch.go_no_go==="go" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-emerald-100 hover:bg-emerald-200 text-emerald-700"}`} data-testid={`go-${ch.id}`}>
                <CheckCircle2 className="w-3 h-3"/>GO
              </Button>
              <Button size="sm" onClick={() => onUpdate({ go_no_go: ch.go_no_go === "no_go" ? null : "no_go" })} className={`text-xs h-7 gap-1 ${ch.go_no_go==="no_go" ? "bg-red-600 hover:bg-red-700 text-white" : "bg-red-100 hover:bg-red-200 text-red-700"}`} data-testid={`no-go-${ch.id}`}>
                <XCircle className="w-3 h-3"/>NO GO
              </Button>
            </>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={onMatch} disabled={busy===ch.id+":match"} data-testid={`match-${ch.id}`} className="gap-1.5 text-xs">
            {busy===ch.id+":match" ? <Loader2 className="w-3 h-3 animate-spin"/> : <Wand2 className="w-3 h-3"/>}
            Matchear con portfolio
          </Button>
          <Button size="sm" variant="outline" onClick={onScout} disabled={busy===ch.id+":scout"} data-testid={`scout-${ch.id}`} className="gap-1.5 text-xs">
            {busy===ch.id+":scout" ? <Loader2 className="w-3 h-3 animate-spin"/> : <Sparkles className="w-3 h-3"/>}
            Scouting IA
          </Button>
          {linkedId ? (
            <span className="text-[10px] px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 flex items-center gap-1 font-semibold">
              <Briefcase className="w-3 h-3"/> En portfolio
            </span>
          ) : (
            <Button size="sm" variant="outline" onClick={onCreateInitiative} disabled={busy===ch.id+":init"} data-testid={`create-init-${ch.id}`} className="gap-1.5 text-xs border-emerald-500/30 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/10">
              {busy===ch.id+":init" ? <Loader2 className="w-3 h-3 animate-spin"/> : <Briefcase className="w-3 h-3"/>}
              Crear iniciativa
            </Button>
          )}
        </div>

        {isCommittee && votingCriteria && votingCriteria.length > 0 && (
          <NovaVotingPanel challenge={ch} criteria={votingCriteria} token={token} onSaved={onReload}/>
        )}

        {matches.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-emerald-600 dark:text-emerald-400 font-bold mb-1">Matches en portfolio</div>
            <div className="space-y-1.5">
              {matches.map((m,i) => (
                <div key={i} className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-500/5 px-3 py-2 text-xs" data-testid={`match-item-${ch.id}-${i}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold truncate">{m.name}</span>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">{m.score || 0}%</span>
                      {linkedId === m.id ? (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-600 text-white font-bold" data-testid={`match-linked-${ch.id}-${i}`}>✓ vinculado</span>
                      ) : linkedId ? null : (
                        <button
                          onClick={() => onLinkMatch && onLinkMatch(m)}
                          disabled={busy === ch.id + ":link"}
                          className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 font-bold disabled:opacity-50"
                          data-testid={`match-link-${ch.id}-${i}`}
                          title="Vincula este reto a la iniciativa/proyecto existente en el portfolio"
                        >
                          {busy === ch.id + ":link" ? "…" : "Vincular"}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="text-muted-foreground mt-0.5">{m.kind} · {m.why}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {scouting.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-violet-600 dark:text-violet-400 font-bold mb-1">Scouting de mercado</div>
            <div className="space-y-1.5">
              {scouting.map((s,i) => (
                <div key={i} className="rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50/50 dark:bg-violet-500/5 px-3 py-2 text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold">{s.name}</span>
                    {s.url && <a href={s.url} target="_blank" rel="noreferrer" className="text-[10px] text-violet-600 dark:text-violet-400 hover:underline">enlace ↗</a>}
                  </div>
                  <div className="text-muted-foreground mt-0.5">{s.country} · {s.maturity}</div>
                  <div className="text-foreground/80 mt-1">{s.summary}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </article>
  );
}

function InviteesTab({ campaign, token, onChange }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const add = async () => {
    if (!name || !email) return;
    const r = await fetch(`${BACKEND_URL}/api/nova/campaigns/${campaign.id}/invitees`, {
      method:"POST", headers:{"Content-Type":"application/json", Authorization:`Bearer ${token}`},
      body: JSON.stringify({ full_name: name, email })
    });
    if (r.ok) { setName(""); setEmail(""); toast.success("Invitado añadido"); onChange(); }
    else toast.error("Email ya añadido");
  };
  const remove = async (id) => {
    await fetch(`${BACKEND_URL}/api/nova/invitees/${id}`, { method:"DELETE", headers:{Authorization:`Bearer ${token}`}});
    onChange();
  };
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-border p-4 flex gap-2 items-end">
        <div className="flex-1"><label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Nombre</label>
          <input className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm" value={name} onChange={(e)=>setName(e.target.value)} data-testid="invitee-name"/></div>
        <div className="flex-1"><label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Email</label>
          <input className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm" value={email} onChange={(e)=>setEmail(e.target.value)} data-testid="invitee-email"/></div>
        <Button onClick={add} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="add-invitee"><Plus className="w-4 h-4"/></Button>
      </div>
      <div className="rounded-2xl border border-border overflow-hidden">
        {(campaign.invitees||[]).map(i => (
          <div key={i.id} className="flex items-center justify-between px-4 py-2 border-b border-border last:border-b-0 hover:bg-muted/30">
            <div>
              <div className="text-sm font-medium">{i.full_name}</div>
              <div className="text-xs text-muted-foreground">{i.email}</div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                i.status==="responded"?"bg-emerald-500/10 text-emerald-700 dark:text-emerald-400":
                i.status==="invited"?"bg-blue-500/10 text-blue-700 dark:text-blue-400":
                "bg-muted text-muted-foreground"}`}>{i.status}</span>
              <button onClick={()=>remove(i.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4"/></button>
            </div>
          </div>
        ))}
        {!(campaign.invitees||[]).length && <div className="p-6 text-center text-muted-foreground text-sm">Aún no hay invitados. Añade tus champions arriba.</div>}
      </div>
    </div>
  );
}

function EmailTab({ campaign, token }) {
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const iframeRef = useRef(null);
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/nova/campaigns/${campaign.id}/email-preview?base_url=${encodeURIComponent(window.location.origin)}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r=>r.json()).then(setPreview);
  }, [campaign.id, token]);

  useEffect(() => {
    if (preview && iframeRef.current) {
      const doc = iframeRef.current.contentDocument;
      doc.open(); doc.write(preview.html); doc.close();
    }
  }, [preview]);

  const send = async () => {
    if (!window.confirm("¿Enviar la invitación a TODOS los invitados pendientes?")) return;
    setSending(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/nova/campaigns/${campaign.id}/send-invites`, {
        method:"POST", headers:{"Content-Type":"application/json", Authorization:`Bearer ${token}`},
        body: JSON.stringify({ only_pending: true, public_base_url: window.location.origin })
      });
      const d = await r.json();
      if (r.ok) toast.success(`Enviado a ${d.sent} invitados`);
      else toast.error(d.detail || "Error al enviar");
    } finally { setSending(false); }
  };

  const pending = (campaign.invitees||[]).filter(i => i.status === "pending").length;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4">
      <div className="rounded-2xl border border-border overflow-hidden bg-slate-50">
        {preview ? (
          <iframe ref={iframeRef} title="Email preview" className="w-full" style={{height:"800px",border:0}}/>
        ) : <div className="p-8 text-center text-muted-foreground">Cargando previsualización…</div>}
      </div>
      <div className="space-y-3 w-full lg:w-80">
        <div className="rounded-2xl border border-border p-4 bg-card">
          <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-2">Enlace público</div>
          <code className="text-xs break-all">{preview?.public_url}</code>
        </div>
        <div className="rounded-2xl border border-emerald-200 dark:border-emerald-800 p-4 bg-emerald-50/50 dark:bg-emerald-500/5">
          <div className="text-sm font-bold mb-1">Enviar invitación WOW</div>
          <div className="text-xs text-muted-foreground mb-3">{pending} invitados pendientes</div>
          <Button onClick={send} disabled={sending || pending===0} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white gap-2" data-testid="send-emails">
            {sending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>} Enviar a {pending} pendientes
          </Button>
        </div>
        <div className="rounded-2xl border border-border p-4 bg-card text-xs text-muted-foreground space-y-2">
          <div className="font-bold text-foreground">Plantilla incluye:</div>
          <ul className="space-y-1 list-disc pl-4">
            <li>Hero verde Valoriza + título</li>
            <li>Brief editable («Millones de gracias»)</li>
            <li>CTA pill al enlace público</li>
            <li>Contraseña destacada (si es autogenerada)</li>
            <li>Tracking de aperturas y clicks (Mandrill)</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function SubmissionsTab({ campaign }) {
  const { token } = useAuth();
  const subs = campaign.submissions || [];
  const exportXlsx = () => {
    const url = `${BACKEND_URL}/api/nova/campaigns/${campaign.id}/submissions/export.xlsx`;
    // Authenticated download
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `nova-respuestas-${campaign.slug || campaign.id}.xlsx`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 4000);
      });
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">{subs.length} respuestas públicas</div>
        <Button onClick={exportXlsx} size="sm" variant="outline" className="gap-2" data-testid="export-xlsx">
          <FileSpreadsheet className="w-4 h-4"/> Export Excel
        </Button>
      </div>
      {!subs.length && <div className="text-muted-foreground text-sm p-6 text-center border border-dashed border-border rounded-2xl">Aún no hay respuestas públicas.</div>}
      {subs.map((s) => (
        <div key={s.id} className="rounded-2xl border border-border bg-card p-4">
          <div className="flex justify-between text-sm">
            <div className="font-semibold">{s.full_name || "Anónimo"} {s.email && <span className="text-muted-foreground">· {s.email}</span>}</div>
            <div className="text-xs text-muted-foreground">{new Date(s.created_at).toLocaleString()}</div>
          </div>
          {s.challenges?.length>0 && <div className="mt-2"><span className="text-[10px] uppercase font-bold text-muted-foreground">Retos:</span>
            <ul className="mt-1 space-y-0.5 text-sm">{s.challenges.map((c,i)=><li key={i}>• {c.title}</li>)}</ul></div>}
          {s.ideas?.length>0 && <div className="mt-2"><span className="text-[10px] uppercase font-bold text-muted-foreground">Ideas:</span>
            <ul className="mt-1 space-y-0.5 text-sm">{s.ideas.map((c,i)=><li key={i}>• {c.title}</li>)}</ul></div>}
          {s.interests?.length>0 && <div className="mt-2 flex gap-1.5 flex-wrap">{s.interests.map((t,i)=><span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-muted">{t}</span>)}</div>}
        </div>
      ))}
    </div>
  );
}

function FiltersBar({ filters, setFilters, areas, champions, total, filtered }) {
  const reset = () => setFilters({ area: "", champion: "", status: "", priority: "", minVotes: "", hasMatch: "", phase: "", goNoGo: "" });
  const hasFilters = Object.values(filters).some(v => v);
  return (
    <div className="rounded-2xl border border-border bg-muted/30 px-4 py-3 flex flex-wrap items-center gap-2">
      <Filter className="w-4 h-4 text-muted-foreground"/>
      <Sel value={filters.area} onChange={(v)=>setFilters({...filters,area:v})} placeholder="Área" options={areas} testid="filter-area"/>
      <Sel value={filters.champion} onChange={(v)=>setFilters({...filters,champion:v})} placeholder="Champion" options={champions} testid="filter-champion"/>
      <Sel value={filters.priority} onChange={(v)=>setFilters({...filters,priority:v})} placeholder="Prioridad" options={["alta","media","baja"]} testid="filter-priority"/>
      <Sel value={filters.status} onChange={(v)=>setFilters({...filters,status:v})} placeholder="Estado" options={STATUSES} testid="filter-status" labelMap={STATUS_LABELS}/>
      <input type="number" min={0} placeholder="Min votos" value={filters.minVotes} onChange={(e)=>setFilters({...filters,minVotes:e.target.value})}
        className="w-24 px-2 py-1 text-xs rounded-lg border border-border bg-background" data-testid="filter-minvotes"/>
      {hasFilters && (
        <button onClick={reset} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="filter-reset">
          <X className="w-3 h-3"/> Limpiar
        </button>
      )}
      <div className="ml-auto text-xs text-muted-foreground"><strong>{filtered}</strong> / {total} retos</div>
    </div>
  );
}

function Sel({ value, onChange, placeholder, options, testid, labelMap }) {
  return (
    <select value={value} onChange={(e)=>onChange(e.target.value)} className="text-xs px-2 py-1 rounded-lg border border-border bg-background max-w-[180px]" data-testid={testid}>
      <option value="">{placeholder}</option>
      {options.map(o => <option key={o} value={o}>{labelMap?.[o] || o}</option>)}
    </select>
  );
}

// ─────────────────────────────────────────────────────────────
// Sub-tabs NOVA: Portfolio · Dealflow · Scouting · Observatorio
// ─────────────────────────────────────────────────────────────

function PortfolioTab({ campaign, challenges }) {
  const navigate = useNavigate();
  return (
    <div className="space-y-3" data-testid="tab-portfolio-content">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          <strong>{challenges.length}</strong> retos ya vinculados a iniciativas del portfolio
        </div>
        <Button size="sm" variant="outline" onClick={() => navigate("/portfolio")} className="gap-1 text-xs">
          <Briefcase className="w-3.5 h-3.5"/> Abrir Portfolio completo
        </Button>
      </div>
      {!challenges.length && (
        <div className="text-center text-muted-foreground text-sm py-10 border border-dashed border-border rounded-2xl">
          Aún no hay retos vinculados a iniciativas de portfolio. Usa &quot;Crear iniciativa&quot; en cada reto para poblar esta vista.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {challenges.map((ch) => (
          <div key={ch.id} className="rounded-2xl border border-border bg-card p-4" data-testid={`portfolio-item-${ch.id}`}>
            <div className="flex items-center gap-2 mb-1">
              <Briefcase className="w-3.5 h-3.5 text-emerald-600"/>
              <span className="text-[10px] uppercase font-bold text-emerald-700 dark:text-emerald-400">En portfolio</span>
              {ch.area && <span className="text-[10px] text-muted-foreground">· {ch.area}</span>}
            </div>
            <h4 className="font-semibold text-sm">{ch.title}</h4>
            {ch.champion && <div className="text-xs text-muted-foreground mt-1">👤 {ch.champion}</div>}
            {ch.linked_initiative_id && (
              <Button size="sm" variant="link" onClick={() => navigate(`/portfolio/${ch.linked_initiative_id}`)} className="text-xs h-6 p-0 mt-1">
                Ver iniciativa vinculada ↗
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function DealflowTab({ campaign, token, onReload }) {
  // Dealflow compartido de equipo: todos los retos en fase Diferenciación (independiente de GO/NO GO)
  const items = useMemo(() => {
    return (campaign.challenges || [])
      .filter(ch => ch.phase === "diferenciacion")
      .sort((a, b) => (b.committee_score?.overall || 0) - (a.committee_score?.overall || 0));
  }, [campaign]);

  const updateGoNoGo = async (chId, go_no_go) => {
    await fetch(`${BACKEND_URL}/api/nova/challenges/${chId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ go_no_go }),
    });
    onReload();
  };
  const updatePhase = async (chId, phase) => {
    await fetch(`${BACKEND_URL}/api/nova/challenges/${chId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ phase }),
    });
    onReload();
  };

  const groups = [
    { key: "pending", label: "Pendientes GO/NO GO", color: "#f59e0b", filter: (i) => !i.go_no_go },
    { key: "go", label: "GO — listos para Comité", color: "#10b981", filter: (i) => i.go_no_go === "go" },
    { key: "no_go", label: "NO GO — descartados", color: "#ef4444", filter: (i) => i.go_no_go === "no_go" },
  ];

  return (
    <div className="space-y-3" data-testid="tab-dealflow-content">
      <div className="text-sm text-muted-foreground">
        <strong>{items.length}</strong> retos en fase Diferenciación (vista compartida por todo el equipo)
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {groups.map((g) => {
          const list = items.filter(g.filter);
          return (
            <div key={g.key} className="rounded-2xl border border-border bg-muted/20 p-3">
              <div className="flex items-center gap-2 mb-2 pb-2 border-b border-border">
                <span className="w-2.5 h-2.5 rounded-full" style={{background: g.color}}/>
                <span className="font-bold text-sm" style={{color: g.color}}>{g.label}</span>
                <span className="text-xs text-muted-foreground ml-auto">{list.length}</span>
              </div>
              <div className="space-y-2">
                {list.map(ch => (
                  <div key={ch.id} className="rounded-lg border border-border bg-card p-2.5 space-y-1" data-testid={`dealflow-item-${ch.id}`}>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <code className="text-[9px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">#{ch.n}</code>
                      {ch.area && <span className="text-[9px] uppercase tracking-wider text-muted-foreground">{ch.area}</span>}
                      {ch.committee_score?.count > 0 && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-700 font-bold">{ch.committee_score.overall}/5</span>
                      )}
                    </div>
                    <div className="text-xs font-semibold leading-snug">{ch.title}</div>
                    {ch.champion && <div className="text-[10px] text-muted-foreground">👤 {ch.champion}</div>}
                    <div className="flex flex-wrap gap-1 pt-1">
                      {g.key !== "go" && (
                        <button onClick={() => updateGoNoGo(ch.id, "go")} className="text-[9px] px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200 font-bold" data-testid={`dealflow-go-${ch.id}`}>
                          GO
                        </button>
                      )}
                      {g.key !== "no_go" && (
                        <button onClick={() => updateGoNoGo(ch.id, "no_go")} className="text-[9px] px-2 py-0.5 rounded bg-red-100 text-red-700 hover:bg-red-200 font-bold" data-testid={`dealflow-nogo-${ch.id}`}>
                          NO GO
                        </button>
                      )}
                      {g.key === "go" && (
                        <button onClick={() => updatePhase(ch.id, "comite")} className="text-[9px] px-2 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 font-bold" data-testid={`dealflow-to-comite-${ch.id}`}>
                          → Pasar a Comité
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {!list.length && <div className="text-[11px] text-muted-foreground text-center py-4">Sin retos</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ScoutingTab({ campaign }) {
  // Aggregate all scouting results across challenges
  const items = useMemo(() => {
    const acc = [];
    (campaign.challenges || []).forEach(ch => {
      (ch.scouting?.results || []).forEach(s => {
        acc.push({ ...s, _challenge: { id: ch.id, title: ch.title, n: ch.n } });
      });
    });
    return acc;
  }, [campaign]);
  return (
    <div className="space-y-3" data-testid="tab-scouting-content">
      <div className="text-sm text-muted-foreground">
        <strong>{items.length}</strong> resultados de scouting agregados (Perplexity IA)
      </div>
      {!items.length && (
        <div className="text-center text-muted-foreground text-sm py-10 border border-dashed border-border rounded-2xl">
          Aún no hay resultados de scouting. Ejecuta &quot;Scouting IA&quot; desde cada reto para poblar esta vista.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((s, i) => (
          <div key={i} className="rounded-2xl border border-violet-200 dark:border-violet-800 bg-violet-50/30 dark:bg-violet-500/5 p-4 space-y-1" data-testid={`scouting-item-${i}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-semibold">{s.name}</div>
              {s.url && <a href={s.url} target="_blank" rel="noreferrer" className="text-[10px] text-violet-700 hover:underline">enlace ↗</a>}
            </div>
            <div className="text-[11px] text-muted-foreground">{s.country || "—"} · {s.maturity || "—"}</div>
            <div className="text-xs text-foreground/80">{s.summary}</div>
            <div className="text-[10px] text-muted-foreground pt-1 border-t border-border">
              Reto #{s._challenge.n} · {s._challenge.title}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ObservatorioTab({ campaign, token }) {
  const [items, setItems] = useState([]);
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/ecosystem/entities`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) {
          const d = await r.json();
          setItems(d.entities || d || []);
        }
      } finally { setLoading(false); }
    })();
  }, [token]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    let list = query
      ? items.filter(x => Object.values(x).some(v => String(v || "").toLowerCase().includes(query)))
      : items;
    list = [...list].sort((a, b) => {
      const av = String(a[sortKey] || "").toLowerCase();
      const bv = String(b[sortKey] || "").toLowerCase();
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [items, q, sortKey, sortDir]);

  const cols = [
    { key: "name", label: "Nombre" },
    { key: "entity_type", label: "Tipo" },
    { key: "sector", label: "Sector" },
    { key: "country", label: "País" },
    { key: "description", label: "Descripción" },
  ];

  const setSort = (k) => {
    if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("asc"); }
  };

  return (
    <div className="space-y-3" data-testid="tab-observatorio-content">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <Building2 className="w-4 h-4"/> <strong>{filtered.length}</strong> / {items.length} entidades del Observatorio (BD interna)
        </div>
        <input
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar en todas las columnas…"
          className="max-w-xs px-3 py-1.5 rounded-lg border border-border bg-background text-sm"
          data-testid="observatorio-search"
        />
      </div>
      {loading && <div className="text-sm text-muted-foreground py-10 text-center">Cargando observatorio…</div>}
      {!loading && !items.length && (
        <div className="text-center text-muted-foreground text-sm py-10 border border-dashed border-border rounded-2xl">
          Observatorio vacío. Añade entidades desde /ecosistema o desde Deep Research.
        </div>
      )}
      {!loading && items.length > 0 && (
        <div className="rounded-2xl border border-border overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/40">
              <tr>
                {cols.map(col => (
                  <th key={col.key} onClick={() => setSort(col.key)} className="text-left px-3 py-2 font-bold cursor-pointer select-none hover:bg-muted/60 whitespace-nowrap" data-testid={`observatorio-col-${col.key}`}>
                    {col.label} {sortKey === col.key && (sortDir === "asc" ? "↑" : "↓")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((row, i) => (
                <tr key={row.id || i} className="border-t border-border hover:bg-muted/20" data-testid={`observatorio-row-${i}`}>
                  {cols.map(col => (
                    <td key={col.key} className="px-3 py-2 max-w-[280px] truncate" title={row[col.key] || ""}>
                      {row[col.key] || <span className="text-muted-foreground">—</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

